import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Dialog Component
 *
 * A modal dialog component for overlaying content.
 * Built on top of Radix UI Dialog primitive.
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger>Open Dialog</DialogTrigger>
 *   <DialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Title</DialogTitle>
 *       <DialogDescription>Description</DialogDescription>
 *     </DialogHeader>
 *     <DialogFooter>
 *       <Button>Save</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}
Dialog.displayName = "Dialog";

/**
 * DialogTrigger Component
 *
 * The trigger element that opens the dialog.
 */
function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}
DialogTrigger.displayName = "DialogTrigger";

/**
 * DialogPortal Component
 *
 * Portal for rendering dialog content.
 */
function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}
DialogPortal.displayName = "DialogPortal";

/**
 * DialogClose Component
 *
 * Close button for the dialog.
 */
function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}
DialogClose.displayName = "DialogClose";

/**
 * DialogOverlay Component
 *
 * Backdrop overlay for the dialog.
 */
function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 setu:fixed setu:inset-0 setu:z-50 setu:bg-black/50",
                className
            )}
            {...props}
        />
    );
}
DialogOverlay.displayName = "DialogOverlay";

/**
 * DialogContent Component
 *
 * Main content container for the dialog.
 */
function DialogContent({
    className,
    children,
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
}) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    "setu:bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 setu:fixed setu:top-[50%] setu:left-[50%] setu:z-50 setu:grid setu:w-full setu:max-w-[calc(100%-2rem)] setu:translate-x-[-50%] setu:translate-y-[-50%] setu:gap-4 setu:rounded-lg setu:!border setu:p-6 setu:shadow-lg setu:duration-200 setu:sm:max-w-lg",
                    className
                )}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className="setu:ring-offset-background setu:focus:ring-ring setu:data-[state=open]:bg-accent setu:data-[state=open]:text-muted-foreground setu:absolute setu:top-4 setu:right-4 setu:rounded-xs setu:opacity-70 setu:transition-opacity setu:hover:opacity-100 setu:focus:ring-2 setu:focus:ring-offset-2 setu:outline-hidden setu:disabled:pointer-events-none setu:[&_svg]:pointer-events-none setu:[&_svg]:shrink-0 setu:[&_svg:not([class*='size-'])]:size-4"
                    >
                        <XIcon />
                        <span className="setu:sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}
DialogContent.displayName = "DialogContent";

/**
 * DialogHeader Component
 *
 * Header section of the dialog.
 */
function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-header"
            className={cn("setu:flex setu:flex-col setu:gap-2 setu:text-center setu:sm:text-left", className)}
            {...props}
        />
    );
}
DialogHeader.displayName = "DialogHeader";

/**
 * DialogFooter Component
 *
 * Footer section of the dialog.
 */
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn("setu:flex setu:flex-col-reverse setu:gap-2 setu:sm:flex-row setu:sm:justify-end", className)}
            {...props}
        />
    );
}
DialogFooter.displayName = "DialogFooter";

/**
 * DialogTitle Component
 *
 * Title of the dialog.
 */
function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn("setu:text-lg setu:leading-none setu:font-semibold", className)}
            {...props}
        />
    );
}
DialogTitle.displayName = "DialogTitle";

/**
 * DialogDescription Component
 *
 * Description text for the dialog.
 */
function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn("setu:text-muted-foreground setu:text-sm", className)}
            {...props}
        />
    );
}
DialogDescription.displayName = "DialogDescription";

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
